import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  History,
  Layers,
  Loader2,
  Package,
  ShoppingCart,
  Users,
  Warehouse,
  type LucideIcon,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogOverlay,
  DialogPortal,
} from '@/components/ui/dialog';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { STATIC_ACTIONS } from './static-actions';
import { filterStaticActions } from './search-utils';
import { useRecentItems } from './use-recent-items';
import {
  CATEGORY_LABELS,
  ENTITY_LABELS,
  type ActionCategory,
  type EntityType,
  type RecentItem,
  type SearchResult,
  type StaticAction,
} from './types';

const ITEM_CLASS = cn(
  'flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm cursor-pointer',
  'text-foreground aria-selected:bg-accent aria-selected:text-accent-foreground',
);

const GROUP_CLASS =
  'px-1 pb-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground';

const ENTITY_ICONS: Record<EntityType, LucideIcon> = {
  product: Package,
  variant: Layers,
  order: ShoppingCart,
  customer: Users,
  inventory: Warehouse,
  sync_event: History,
};

interface CommandPaletteProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ isOpen, onOpenChange }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [entityQuery, setEntityQuery] = useState('');
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { setTheme } = useTheme();
  const { addRecentItem, recentItems } = useRecentItems();
  const listRef = useRef<HTMLDivElement>(null);

  // Reset query when closing
  useEffect(() => {
    if (!isOpen) {
      setQuery('');
      setEntityQuery('');
    }
  }, [isOpen]);

  // Avoid issuing a database request for every keystroke. Two characters is
  // enough for useful exact/prefix matches without returning huge one-letter
  // result sets.
  useEffect(() => {
    const trimmed = query.trim();
    if (!isOpen || trimmed.length < 2) {
      setEntityQuery('');
      return;
    }
    const timer = window.setTimeout(() => setEntityQuery(trimmed), 250);
    return () => window.clearTimeout(timer);
  }, [isOpen, query]);

  const {
    data: entityResults = [],
    isFetching: isSearchingEntities,
    isError: entitySearchFailed,
  } = useGlobalSearch(entityQuery, isOpen);

  // Reset scroll when results change
  useLayoutEffect(() => {
    listRef.current?.scrollTo(0, 0);
  }, [query]);

  const filteredActions = filterStaticActions(STATIC_ACTIONS, query);
  const hasQuery = query.trim().length > 0;

  // Group actions by category
  const groupedActions = filteredActions.reduce<
    Record<ActionCategory, StaticAction[]>
  >(
    (groups, action) => {
      groups[action.category].push(action);
      return groups;
    },
    { navigation: [], action: [], theme: [], utility: [] },
  );

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  function handleActionSelect(action: StaticAction): void {
    if (action.url) {
      navigate(action.url);
      close();
      return;
    }

    switch (action.action) {
      case 'theme-light':
        setTheme('light');
        close();
        break;
      case 'theme-dark':
        setTheme('dark');
        close();
        break;
      case 'theme-system':
        setTheme('system');
        close();
        break;
      case 'trigger-sync':
        navigate('/settings');
        close();
        break;
      case 'logout':
        signOut();
        close();
        break;
    }
  }

  function handleRecentSelect(item: RecentItem): void {
    addRecentItem(item);
    navigate(item.url);
    close();
  }

  function handleEntitySelect(item: SearchResult): void {
    addRecentItem({
      id: item.id,
      name: item.name,
      secondary: item.secondary,
      type: item.type,
      url: item.url,
    });
    navigate(item.url);
    close();
  }

  const waitingForEntitySearch =
    hasQuery &&
    query.trim().length >= 2 &&
    (query.trim() !== entityQuery || isSearchingEntities);
  const visibleEntityResults =
    query.trim() === entityQuery ? entityResults : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[100]" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-4 z-[100] w-[calc(100vw-1rem)] max-w-[560px] -translate-x-1/2 sm:top-[20%] sm:w-full',
            'rounded-lg border border-border bg-popover shadow-2xl',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
          )}
        >
          <DialogPrimitive.Title className="sr-only">
            Command menu
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Search products, variants, orders, customers, inventory, sync history, navigation, and app actions.
          </DialogPrimitive.Description>
          <Command shouldFilter={false} className="flex flex-col">
            <div className="flex items-center border-b border-border px-3">
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Type a command or search..."
                className={cn(
                  'flex h-12 w-full bg-transparent py-3 text-base sm:h-11 sm:text-sm',
                  'text-foreground placeholder:text-muted-foreground',
                  'outline-none disabled:cursor-not-allowed disabled:opacity-50',
                )}
              />
            </div>

            <Command.List
              ref={listRef}
              className="max-h-[min(60dvh,420px)] overflow-y-auto p-1.5 sm:max-h-[320px]"
            >
              <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
                {entitySearchFailed ? (
                  'Record search is temporarily unavailable.'
                ) : waitingForEntitySearch ? (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Searching…
                  </span>
                ) : query.trim().length === 1 ? (
                  'Type one more character to search records.'
                ) : (
                  'No results found.'
                )}
              </Command.Empty>

              {/* Recent items (idle state only) */}
              {!hasQuery && recentItems.length > 0 && (
                <Command.Group heading="Recent" className={GROUP_CLASS}>
                  {recentItems.slice(0, 10).map((item) => {
                    const Icon = ENTITY_ICONS[item.type] ?? Clock;
                    return (
                      <Command.Item
                        key={`recent-${item.id}`}
                        value={`recent-${item.id}`}
                        onSelect={() => handleRecentSelect(item)}
                        className={ITEM_CLASS}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{item.name}</span>
                        {item.secondary && (
                          <span className="ml-auto truncate text-xs text-muted-foreground">
                            {item.secondary}
                          </span>
                        )}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              )}

              {hasQuery && visibleEntityResults.length > 0 && (
                <Command.Group heading="Search results" className={GROUP_CLASS}>
                  {visibleEntityResults.map((item) => {
                    const Icon = ENTITY_ICONS[item.type];
                    return (
                      <Command.Item
                        key={item.id}
                        value={item.id}
                        onSelect={() => handleEntitySelect(item)}
                        className={ITEM_CLASS}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate">{item.name}</span>
                          {item.secondary && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {item.secondary}
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-[10px] text-muted-foreground">
                          {ENTITY_LABELS[item.type]}
                        </span>
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              )}

              {/* Static actions grouped by category */}
              {(
                Object.entries(groupedActions) as [
                  ActionCategory,
                  StaticAction[],
                ][]
              ).map(
                ([category, actions]) =>
                  actions.length > 0 && (
                    <Command.Group
                      key={category}
                      heading={CATEGORY_LABELS[category]}
                      className={GROUP_CLASS}
                    >
                      {actions.map((action) => (
                        <Command.Item
                          key={action.id}
                          value={action.id}
                          onSelect={() => handleActionSelect(action)}
                          className={ITEM_CLASS}
                        >
                          <action.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span>{action.label}</span>
                        </Command.Item>
                      ))}
                    </Command.Group>
                  ),
              )}
            </Command.List>

            {/* Footer with keyboard hints */}
            <div className="hidden items-center justify-between border-t border-border px-3 py-2 sm:flex">
              <span className="text-[11px] text-muted-foreground">
                Navigate with{' '}
                <kbd className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                  &uarr;
                </kbd>{' '}
                <kbd className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                  &darr;
                </kbd>
              </span>
              <span className="text-[11px] text-muted-foreground">
                Select with{' '}
                <kbd className="rounded bg-muted px-1 py-0.5 font-mono text-[10px]">
                  &crarr;
                </kbd>
              </span>
            </div>
          </Command>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
